/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.7.5
@license MIT
@copyright (c) 2017, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Promise = _interopDefault(require('babel-runtime/core-js/promise'));
var _Object$values = _interopDefault(require('babel-runtime/core-js/object/values'));
var _isEqual = _interopDefault(require('lodash/fp/isEqual'));
var __ol = _interopDefault(require('openlayers'));
var uuid = _interopDefault(require('uuid/v4'));
var Observable = require('rxjs/Observable');
var merge = require('rxjs/observable/merge');
var core = require('../core');

var Overlay = __ol.Overlay;
var props = {
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  offset: {
    type: Array,
    default: function _default() {
      return [0, 0];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  position: {
    type: Array,
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  positioning: {
    type: String,
    default: core.OVERLAY_POSITIONING.TOP_LEFT,
    validator: function validator(value) {
      return _Object$values(core.OVERLAY_POSITIONING).includes(value);
    }
  },
  stopEvent: {
    type: Boolean,
    default: true
  },
  insertFirst: Boolean,
  autoPan: Boolean,
  autoPanMargin: {
    type: Number,
    default: 20
  },
  autoPanAnimation: Object
};

var computed = {};

var methods = {
  /**
   * @return {ol.Overlay}
   * @protected
   */
  createOlObject: function createOlObject() {
    return new Overlay({
      id: this.id,
      offset: this.offset,
      position: core.projHelper.fromLonLat(this.position, this.$view.getProjection()),
      positioning: this.positioning,
      stopEvent: this.stopEvent,
      insertFirst: this.insertFirst,
      autoPan: this.autoPan,
      autoPanMargin: this.autoPanMargin,
      autoPanAnimation: this.autoPanAnimation
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    var _this = this;

    core.assert.hasOverlay(this);

    this.$overlay.once('change:element', function () {
      _this.$el.style.visibility = 'visible';
    });
    this.$overlay.setElement(this.$el);
    this.$overlaysContainer && this.$overlaysContainer.addOverlay(this.$overlay);
    this.subscribeAll();
    this.refresh();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    core.assert.hasOverlay(this);

    this.unsubscribeAll();
    this.$overlay.setElement(undefined);
    this.$overlaysContainer && this.$overlaysContainer.removeOverlay(this.$overlay);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToOverlayChanges.call(this);
  },

  /**
   * Refresh internal ol objects
   * @return {Promise}
   */
  refresh: function refresh() {
    var _this2 = this;

    return _Promise.all([core.olCmp.methods.refresh(), new _Promise(function (resolve) {
      _this2.$overlay.once('change:position', function () {
        return resolve();
      });
      _this2.$overlay.setPosition(_this2.$overlay.getPosition().slice());
    })]);
  }
};

var watch = {
  offset: function offset(value) {
    if (this.$overlay && !_isEqual(value, this.$overlay.getOffset())) {
      this.$overlay.setOffset(value);
    }
  },
  position: function position(value) {
    if (this.$overlay && this.$view && !_isEqual(value, core.projHelper.toLonLat(this.$overlay.getPosition(), this.$view.getProjection()))) {
      this.$overlay.setPosition(core.projHelper.fromLonLat(value, this.$view.getProjection()));
    }
  },
  positioning: function positioning(value) {
    if (this.$overlay && value !== this.$overlay.getPositioning()) {
      this.$overlay.setPositioning(value);
    }
  }
};

// todo add scoped slot support?
var Overlay$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('div', { class: _vm.$options.name, staticStyle: { "visibility": "hidden" }, attrs: { "id": [_vm.$options.name, _vm.id].join('-') } }, [_vm._t("default", null, { id: _vm.id, position: _vm.position })], 2);
  }, staticRenderFns: [],
  name: 'vl-overlay',
  mixins: [core.olCmp, core.useMapCmp],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  created: function created() {
    var _this3 = this;

    _Object$defineProperties(this, {
      /**
       * @type {ol.Overlay|undefined}
       */
      $overlay: {
        enumerable: true,
        get: function get() {
          return _this3.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.view;
        }
      },
      $overlaysContainer: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.overlaysContainer;
        }
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToOverlayChanges() {
  var _this4 = this;

  core.assert.hasOverlay(this);
  core.assert.hasView(this);

  var ft = 100;
  var changes = merge.merge.call(Observable.Observable, core.observableFromOlChangeEvent(this.$overlay, 'position', true, ft, getPosition.bind(this)), core.observableFromOlChangeEvent(this.$overlay, ['offset', 'positioning'], true, ft));

  this.subscribeTo(changes, function (_ref) {
    var prop = _ref.prop,
        value = _ref.value;

    ++_this4.rev;
    _this4.$emit('update:' + prop, value);
  });
}

function getPosition() {
  return core.projHelper.toLonLat(this.$overlay.getPosition(), this.$view.getProjection());
}

var index = {
  Overlay: Overlay$1,
  install: function install(Vue) {
    Vue.component(Overlay$1.name, Overlay$1);
  }
};

module.exports = index;

//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/overlay/index.js
// module id = 1191
// module chunks = 1